import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CreateB2BProductInput, B2BProduct } from '@/types/database/b2b-products';
import { toast } from 'sonner';

export const useB2BProducts = () => {
  return useQuery({
    queryKey: ['b2b-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('b2b_products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as B2BProduct[];
    },
  });
};

export const useBulkCreateB2BProducts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (products: CreateB2BProductInput[]) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Add created_by to each product
      const productsWithCreator = products.map(product => ({
        ...product,
        created_by: user.user.id,
      }));

      const { data, error } = await supabase
        .from('b2b_products')
        .insert(productsWithCreator)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['b2b-products'] });
      toast.success(`Successfully uploaded ${data.length} B2B products`);
    },
    onError: (error) => {
      console.error('Error uploading B2B products:', error);
      toast.error('Failed to upload B2B products');
    },
  });
};

export const useCreateB2BProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (product: CreateB2BProductInput) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('b2b_products')
        .insert({
          ...product,
          created_by: user.user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['b2b-products'] });
      toast.success('B2B product created successfully');
    },
    onError: (error) => {
      console.error('Error creating B2B product:', error);
      toast.error('Failed to create B2B product');
    },
  });
};

export const useUpdateB2BProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateB2BProductInput> }) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('b2b_products')
        .update({
          ...updates,
          updated_by: user.user.id,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['b2b-products'] });
      toast.success('B2B product updated successfully');
    },
    onError: (error) => {
      console.error('Error updating B2B product:', error);
      toast.error('Failed to update B2B product');
    },
  });
};

export const useDeleteB2BProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('b2b_products')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['b2b-products'] });
      toast.success('B2B product deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting B2B product:', error);
      toast.error('Failed to delete B2B product');
    },
  });
};